import { getToken } from "./token";
import type { ClientMessage, Envelope } from "./types";
import { SCHEMA_VERSION } from "./types";

export type ConnectionState = "connecting" | "open" | "closed";

/**
 * Субпротокол ограничен token-charset RFC 6455, а операторский токен — нет:
 * с «+», «/» или «=» (обычный base64) конструктор WebSocket бросает SyntaxError
 * и терминал падает. Поэтому токен кодируется base64url без «=», web_api
 * декодирует обратно (wsTokenValid).
 */
function encodeTokenProtocol(token: string): string {
  const bytes = new TextEncoder().encode(token);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

interface Options {
  onMessage: (env: Envelope) => void;
  onState: (state: ConnectionState) => void;
}

/**
 * Клиент торгового сокета.
 *
 * Два свойства, ради которых он написан руками, а не взят готовым:
 *  1. Переподписка после разрыва — набор рынков хранится здесь и восстанавливается
 *     автоматически, иначе после реконнекта оператор молча остаётся без данных.
 *  2. Экспоненциальная пауза с потолком — при падении web_api сотня вкладок не
 *     должна ддосить его переподключениями.
 */
export class TradingSocket {
  private ws: WebSocket | null = null;
  /**
   * Рынок -> число держателей подписки.
   *
   * Счётчик, а не множество: в StrictMode эффект вызывается дважды
   * (retain -> release -> retain), и без счёта размонтирование первой копии
   * снимало бы подписку, нужную второй.
   */
  private readonly markets = new Map<string, number>();
  /** Рынок -> таймер отложенного снятия подписки. */
  private readonly pendingRelease = new Map<string, number>();
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;
  private closedByUs = false;

  /**
   * Выдержка перед снятием подписки.
   *
   * Оператор часто скачет между рынками туда-обратно, а StrictMode монтирует
   * страницу дважды. Без выдержки каждый такой переход давал бы лишнюю пару
   * unsubscribe/subscribe и лишний перезапрос снапшотов.
   */
  private static readonly releaseGraceMs = 2000;

  constructor(private readonly opts: Options) {}

  connect(): void {
    this.closedByUs = false;
    this.opts.onState("connecting");

    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${location.host}/ws`;

    // Токен уходит субпротоколом (Sec-WebSocket-Protocol: bearer, <token>):
    // браузерный WebSocket API не позволяет задать заголовок Authorization,
    // а query-параметр оседал бы в access-логах nginx.
    const token = getToken();
    const ws = token
      ? new WebSocket(url, ["bearer", encodeTokenProtocol(token)])
      : new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempt = 0;
      this.opts.onState("open");
      // Восстанавливаем подписки, накопленные до разрыва. Это и есть критерий
      // приёмки вехи M3: переподключение не требует перезагрузки страницы.
      // Сервер обрабатывает повторный subscribe как no-op, поэтому слать весь
      // набор заново безопасно.
      if (this.markets.size > 0) {
        this.send({ type: "subscribe", markets: [...this.markets.keys()] });
      }
    };

    ws.onmessage = (ev) => {
      let env: Envelope;
      try {
        env = JSON.parse(ev.data as string) as Envelope;
      } catch {
        console.warn("нечитаемое сообщение из сокета", ev.data);
        return;
      }
      // Неизвестная версия схемы игнорируется, а не роняет UI: так выкатка нового
      // формата на бэкенде не ломает открытые вкладки.
      if (env.v !== SCHEMA_VERSION) {
        console.warn("неизвестная версия схемы", env.v);
        return;
      }
      this.opts.onMessage(env);
    };

    ws.onclose = () => {
      this.opts.onState("closed");
      if (!this.closedByUs) this.scheduleReconnect();
    };

    ws.onerror = () => ws.close();
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    const delay = Math.min(1000 * 2 ** this.reconnectAttempt, 15_000);
    this.reconnectAttempt += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /** Заявить интерес к рынку. Подписка уходит на сервер при первом держателе. */
  retain(market: string): void {
    const timer = this.pendingRelease.get(market);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      this.pendingRelease.delete(market);
      // Отложенное снятие счётчик НЕ уменьшало (см. release): держатель-фантом
      // всё ещё учтён, и новый держатель занимает его место. Ещё один инкремент
      // завышал бы счёт навсегда — финальный release уходил бы в ветку n > 1,
      // и подписка не снималась бы никогда.
      return;
    }
    const n = (this.markets.get(market) ?? 0) + 1;
    this.markets.set(market, n);
    if (n === 1) this.send({ type: "subscribe", markets: [market] });
  }

  /** Отказаться от рынка. Подписка снимается после выдержки и только если за
   *  это время не появился новый держатель. */
  release(market: string): void {
    const n = this.markets.get(market);
    if (n === undefined) return;
    if (n > 1) {
      this.markets.set(market, n - 1);
      return;
    }
    if (this.pendingRelease.has(market)) return;

    this.pendingRelease.set(
      market,
      window.setTimeout(() => {
        this.pendingRelease.delete(market);
        // Рынок ОСТАЁТСЯ в this.markets всю выдержку: разрыв, случившийся
        // внутри неё, обязан восстановить подписку вместе с остальными.
        if (this.markets.get(market) !== 1) return;
        this.markets.delete(market);
        this.send({ type: "unsubscribe", markets: [market] });
      }, TradingSocket.releaseGraceMs),
    );
  }

  /** Текущий набор подписок. Для диагностики и тестов. */
  subscribedMarkets(): string[] {
    return [...this.markets.keys()];
  }

  private send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
    // Если сокет закрыт — не буферизуем: подписки восстановятся в onopen,
    // а торговые команды идут через REST, а не через сокет.
  }

  close(): void {
    this.closedByUs = true;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
  }
}
